import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../services/apiClient';
import WorkspacePage from '../components/layout/WorkspacePage';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Pill from '../components/ui/Pill';
import EmptyState from '../components/ui/EmptyState';
import StatusBanner from '../components/ui/StatusBanner';
import './NotificationsInbox.css';

/**
 * PR-3 — workforce notifications inbox.
 *
 * Renders the caller's in_app deliveries with category filter chips,
 * cursor pagination, and a per-row acknowledge action.
 *
 * Tenant + user scope: every list / ack request hits the
 * notifications route which derives companyId + recipientUserId
 * from req.user. The client never sends or shows another tenant's
 * data.
 */

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All', category: null },
  { value: 'unread', label: 'Unread', category: null, unreadOnly: true },
  { value: 'documents', label: 'Documents', category: 'documents' },
  { value: 'vacation', label: 'Vacation', category: 'vacation' },
  { value: 'callout', label: 'Call-outs', category: 'callout' },
  { value: 'integrity', label: 'Integrity', category: 'integrity' }
];

const SEVERITY_TONE = {
  critical: 'danger',
  warn: 'warning',
  info: 'neutral'
};

function NotificationRow({ row, onAcknowledge, onOpenCta, busy }) {
  const subject = row.notification.subjectKey;
  const severity = row.notification.severity;
  const payload = row.notification.payload || {};
  const personName = payload?.person?.displayName || null;
  const employeeNumber = payload?.person?.employeeNumber || null;
  const personLine = personName
    ? [personName, employeeNumber].filter(Boolean).join(' · ')
    : null;
  const ctaPath = payload?.ctaPath || null;
  const ctaLabel = payload?.ctaLabel || 'Open';
  const acknowledged = Boolean(row.acknowledgedAt);

  return (
    <Card
      className={`notifications-row notifications-row--${severity}${acknowledged ? ' notifications-row--read' : ''}`}
      padding="sm"
      shadow="none"
      data-testid={`notifications-row-${row.deliveryId}`}
    >
      <div className="notifications-row-header">
        <Pill variant={SEVERITY_TONE[severity] || 'neutral'}>{severity}</Pill>
        <span className="notifications-row-subject">{subject}</span>
        <span className="notifications-row-time">
          {new Date(row.notification.generatedAt).toLocaleString()}
        </span>
      </div>
      {personLine ? (
        <p className="notifications-row-person">{personLine}</p>
      ) : null}
      <div className="notifications-row-actions">
        {ctaPath ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onOpenCta(row, ctaPath)}
            data-testid={`notifications-row-cta-${row.deliveryId}`}
          >
            {ctaLabel}
          </Button>
        ) : null}
        {!acknowledged ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onAcknowledge(row.deliveryId)}
            disabled={busy}
            data-testid={`notifications-row-ack-${row.deliveryId}`}
          >
            Mark read
          </Button>
        ) : (
          <span
            className="notifications-row-acknowledged"
            data-testid={`notifications-row-acknowledged-${row.deliveryId}`}
          >
            Acknowledged
          </span>
        )}
      </div>
    </Card>
  );
}

function NotificationsInbox({ onNavigate = null }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [acknowledgingId, setAcknowledgingId] = useState(null);

  const filter = useMemo(
    () => CATEGORY_FILTERS.find((f) => f.value === activeFilter) || CATEGORY_FILTERS[0],
    [activeFilter]
  );

  const buildUrl = useCallback((cursor) => {
    const params = new URLSearchParams();
    if (filter.category) params.set('category', filter.category);
    if (filter.unreadOnly) params.set('unread', 'true');
    if (cursor) params.set('cursor', cursor);
    params.set('limit', '25');
    return `/api/notifications?${params.toString()}`;
  }, [filter]);

  const fetchPage = useCallback(async (cursor = null, { append = false } = {}) => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiRequest(buildUrl(cursor));
      setItems((prev) => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      setError(err?.message || 'Unable to load notifications.');
    } finally {
      setIsLoading(false);
    }
  }, [buildUrl]);

  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  const handleAcknowledge = useCallback(async (deliveryId) => {
    setAcknowledgingId(deliveryId);
    try {
      await apiRequest(`/api/notifications/${deliveryId}/acknowledge`, { method: 'POST' });
      setItems((prev) => prev.map((row) => row.deliveryId === deliveryId
        ? { ...row, acknowledgedAt: new Date().toISOString() }
        : row));
    } catch (err) {
      setError(err?.message || 'Unable to acknowledge notification.');
    } finally {
      setAcknowledgingId(null);
    }
  }, []);

  const handleMarkAll = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      await apiRequest('/api/notifications/mark-all-read', { method: 'POST' });
      await fetchPage(null);
    } catch (err) {
      setError(err?.message || 'Unable to mark all read.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage]);

  const handleOpenCta = useCallback((row, ctaPath) => {
    // Acknowledge in the background; navigate immediately so the
    // operator never feels the network call.
    handleAcknowledge(row.deliveryId);
    if (typeof onNavigate === 'function') {
      onNavigate(ctaPath);
    } else {
      window.location.assign(ctaPath);
    }
  }, [handleAcknowledge, onNavigate]);

  return (
    <WorkspacePage
      title="Notifications"
      density="comfortable"
      width="narrative"
      data-testid="notifications-inbox-page"
    >
      <div className="notifications-inbox-toolbar">
        <div className="notifications-inbox-filters" role="tablist" aria-label="Notification filters">
          {CATEGORY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`notifications-inbox-filter${activeFilter === f.value ? ' is-active' : ''}`}
              onClick={() => setActiveFilter(f.value)}
              role="tab"
              aria-selected={activeFilter === f.value}
              data-testid={`notifications-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleMarkAll}
          disabled={isLoading || items.length === 0}
          data-testid="notifications-mark-all-read"
        >
          Mark all read
        </Button>
      </div>

      {error ? <StatusBanner kind="error" message={error} live /> : null}

      {isLoading && items.length === 0 ? (
        <div className="notifications-inbox-loading" aria-live="polite">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="·"
          headline="All clear"
          body="No notifications match the current filter."
        />
      ) : (
        <div className="notifications-inbox-list">
          {items.map((row) => (
            <NotificationRow
              key={row.deliveryId}
              row={row}
              onAcknowledge={handleAcknowledge}
              onOpenCta={handleOpenCta}
              busy={acknowledgingId === row.deliveryId}
            />
          ))}
        </div>
      )}

      {nextCursor ? (
        <div className="notifications-inbox-load-more">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => fetchPage(nextCursor, { append: true })}
            disabled={isLoading}
            data-testid="notifications-load-more"
          >
            Load more
          </Button>
        </div>
      ) : null}
    </WorkspacePage>
  );
}

export default NotificationsInbox;
